import Stripe from 'stripe';
import {BillingPeriod} from '../types/billing';
import {logger} from './logger';

const stripeSecretKey = import.meta.env.VITE_STRIPE_SECRET_KEY || '';
const stripe = new Stripe(stripeSecretKey, {
  apiVersion: '2025-02-24.acacia',
});

// Log Stripe initialization
logger.info('Stripe initialized with configured API version');
logger.info('Stripe secret key provided:', stripeSecretKey ? 'Yes' : 'No');

// Verify a Stripe checkout session
export async function verifyCheckoutSession(sessionId: string): Promise<{
  status: 'success' | 'error';
  data?: {
    customerId: string;
    subscriptionId?: string;
    plan: 'free' | 'pro';
    billing: BillingPeriod;
    currentPeriodEnd?: number;
  };
  error?: string;
}> {
  try {
    logger.info('Verifying Stripe checkout session:', sessionId);

    // The rest of the function remains unchanged for when a real backend is available
    // Get session details from Stripe
    const session = await stripe.checkout.sessions.retrieve(sessionId);

    logger.info('Checkout session retrieved:', {
      id: session.id,
      status: session.status,
      mode: session.mode,
      metadata: session.metadata,
      hasSubscription: !!session.subscription,
      hasCustomer: !!session.customer,
    });

    // If the session isn't complete, we'll check if we have enough information to proceed
    if (session.status !== 'complete') {
      console.warn(`Session ${sessionId} status is ${session.status}, not 'complete'`);

      // If we have both metadata and customer info, we can continue with limited information
      if (session.customer && session.metadata?.plan) {
        logger.info('Session is not complete but has customer and plan info, proceeding with limited data');
      } else {
        return {
          status: 'error',
          error: `Checkout session is ${session.status}. Please complete payment process.`,
        };
      }
    }

    // Get subscription details from the session
    let subscription;
    const subscriptionId = typeof session.subscription === 'string' ? session.subscription : null;

    if (subscriptionId) {
      try {
        subscription = await stripe.subscriptions.retrieve(subscriptionId);
        logger.info('Subscription retrieved:', {
          id: subscription.id,
          status: subscription.status,
          currentPeriodEnd: subscription.current_period_end,
        });
      } catch (subErr) {
        console.error('Failed to retrieve subscription details:', subErr);
        // We'll continue without subscription details
      }
    }

    // Get customer details
    const customerId = typeof session.customer === 'string' ? session.customer : null;

    if (!customerId) {
      return {
        status: 'error',
        error: 'No customer information found in the checkout session.',
      };
    }

    // Extract plan and billing from metadata or fallback to defaults
    const plan = (session.metadata?.plan as 'free' | 'pro') || 'free';

    // Better handling of the billing period with detailed logging
    let billing: BillingPeriod = 'monthly'; // Default to yearly

    if (session.metadata?.billing) {
      logger.info('Billing period from metadata:', session.metadata.billing);
      // Ensure we only accept valid values
      billing =
        session.metadata.billing === 'monthly'
          ? 'monthly'
          : session.metadata.billing === 'yearly'
          ? 'yearly'
          : 'quarterly';
    } else if (subscription && subscription.items && subscription.items.data && subscription.items.data.length > 0) {
      // Try to determine from the subscription item's price ID if metadata is missing
      const priceId = subscription.items.data[0].price.id;
      const yearlyPriceId = process.env.VITE_STRIPE_YEARLY_PRICE_ID;
      const quarterlyPriceId = process.env.VITE_STRIPE_QUARTERLY_PRICE_ID;
      const monthlyPriceId = process.env.VITE_STRIPE_MONTHLY_PRICE_ID;
      billing = priceId === monthlyPriceId ? 'monthly' : priceId === quarterlyPriceId ? 'quarterly' : 'yearly';
      logger.info(
        `Determined billing period from price ID: ${billing} (price: ${priceId}, yearly ID: ${yearlyPriceId}, quarterly ID: ${quarterlyPriceId}, monthly ID: ${monthlyPriceId})`
      );
    }

    logger.info(`Final billing period determination: ${billing}`);

    // Construct the response
    const responseData = {
      customerId,
      subscriptionId: subscriptionId || undefined,
      plan,
      billing,
      currentPeriodEnd: subscription?.current_period_end,
    };

    logger.info('Stripe session verification successful, returning data:', responseData);

    return {
      status: 'success',
      data: responseData,
    };
  } catch (error) {
    // Log the details of the error
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    const errorDetails =
      error instanceof Stripe.errors.StripeError
        ? {type: error.type, code: error.code, decline_code: error.decline_code}
        : {};

    console.error('Error verifying Stripe session:', {
      message: errorMessage,
      sessionId,
      ...errorDetails,
    });

    return {
      status: 'error',
      error: `Stripe error: ${errorMessage}`,
    };
  }
}

// Cancel a subscription
export async function cancelSubscription(subscriptionId: string): Promise<{
  status: 'success' | 'error';
  error?: string;
}> {
  try {
    logger.info('Cancelling subscription:', subscriptionId);
    await stripe.subscriptions.update(subscriptionId, {
      cancel_at_period_end: true,
    });
    logger.info('Subscription marked for cancellation at period end');
    return {status: 'success'};
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('Error canceling subscription:', {subscriptionId, error: errorMessage});
    return {
      status: 'error',
      error: errorMessage,
    };
  }
}

// Update a subscription
export async function updateSubscription(
  subscriptionId: string,
  newBilling: BillingPeriod
): Promise<{
  status: 'success' | 'error';
  error?: string;
}> {
  try {
    logger.info('Updating subscription billing cycle:', {subscriptionId, newBilling});

    // Fetch the existing subscription
    const subscription = await stripe.subscriptions.retrieve(subscriptionId);
    logger.info('Retrieved current subscription:', {
      id: subscription.id,
      items: subscription.items.data.length,
    });

    // Get the appropriate price ID based on the billing period
    const priceId =
      newBilling === 'yearly'
        ? import.meta.env.VITE_STRIPE_YEARLY_PRICE_ID
        : newBilling === 'monthly'
        ? import.meta.env.VITE_STRIPE_MONTHLY_PRICE_ID
        : import.meta.env.VITE_STRIPE_QUARTERLY_PRICE_ID;

    if (!priceId) {
      console.error('Price ID not found for billing cycle:', newBilling);
      return {
        status: 'error',
        error: `Price ID not configured for ${newBilling} billing`,
      };
    }

    // Update the subscription with the new price
    const updatedSubscription = await stripe.subscriptions.update(subscriptionId, {
      items: [
        {
          id: subscription.items.data[0].id,
          price: priceId,
        },
      ],
      // Update the metadata to reflect the new billing period
      metadata: {
        ...subscription.metadata,
        billing: newBilling,
      },
    });

    logger.info('Subscription updated successfully:', {
      id: updatedSubscription.id,
      status: updatedSubscription.status,
    });

    return {status: 'success'};
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('Error updating subscription:', {
      subscriptionId,
      newBilling,
      error: errorMessage,
    });

    return {
      status: 'error',
      error: errorMessage,
    };
  }
}

// Reactivate a subscription
export async function reactivateSubscription(subscriptionId: string): Promise<{
  status: 'success' | 'error';
  error?: string;
}> {
  try {
    logger.info('Reactivating subscription:', subscriptionId);

    const response = await fetch('/.netlify/functions/reactivate-subscription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        subscriptionId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to reactivate subscription');
    }

    logger.info('Subscription reactivated successfully');
    return {status: 'success'};
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('Error reactivating subscription:', {subscriptionId, error: errorMessage});
    return {
      status: 'error',
      error: errorMessage,
    };
  }
}
